(() => {
  if (window.cpMobileDrawerOverlayInitialized) return;
  window.cpMobileDrawerOverlayInitialized = true;

  const closeIcon = `
    <svg aria-hidden="true" focusable="false" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 16L17 1M1 1L17 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;

  const enhanceDrawer = (root = document) => {
    root.querySelectorAll('header-drawer').forEach((drawer) => {
      if (drawer.dataset.cpOverlayReady === 'true') return;

      const details = drawer.querySelector(':scope > details');
      const summary = details?.querySelector(':scope > summary');
      const panel = details?.querySelector(':scope > .menu-drawer');
      if (!details || !summary || !panel) return;

      drawer.dataset.cpOverlayReady = 'true';

      let backdrop = details.querySelector(':scope > .cp-menu-drawer__backdrop');
      if (!backdrop) {
        backdrop = document.createElement('button');
        backdrop.type = 'button';
        backdrop.className = 'cp-menu-drawer__backdrop';
        backdrop.setAttribute('aria-label', 'Close menu');
        backdrop.setAttribute('tabindex', '-1');
        details.insertBefore(backdrop, panel);
      }

      let closeButton = panel.querySelector('.cp-menu-drawer__close-button');
      if (!closeButton) {
        closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'cp-menu-drawer__close-button';
        closeButton.setAttribute('aria-label', 'Close menu');
        closeButton.innerHTML = closeIcon;
        panel.prepend(closeButton);
      }

      const closeDrawer = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (typeof drawer.closeMenuDrawer === 'function') {
          drawer.closeMenuDrawer(event, summary);
          return;
        }

        details.classList.remove('menu-opening');
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
      };

      backdrop.addEventListener('click', closeDrawer);
      closeButton.addEventListener('click', closeDrawer);
    });
  };

  const init = () => enhanceDrawer(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => enhanceDrawer(event.target));
})();

/*
  Mobile storefront/Preview hardening for Dawn's "Always" sticky mode.

  Theme Editor:
  - Dawn stays completely native because it already behaves correctly there.

  Standalone mobile storefront/Preview:
  - Dawn's browser-sticky positioning is disabled before activation.
  - The header's real normal-flow position is measured once.
  - The whole section becomes fixed when its original top reaches the sticky offset.
  - The frosted/scrolled state activates when the original header bottom is passed.
  - Scroll direction never controls visibility.
*/
(() => {
  if (window.cpAlwaysStickyHeaderInitialized) return;
  window.cpAlwaysStickyHeaderInitialized = true;

  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const metrics = new WeakMap();
  let frameId = 0;

  const isThemeEditor = () => Boolean(window.Shopify && window.Shopify.designMode);
  const getScrollTop = () =>
    Math.max(0, window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0);

  const getStickyOffset = () => 0;

  const getAlwaysStickyHeaders = () =>
    document.querySelectorAll(
      'sticky-header[data-sticky-type="always"], sticky-header[data-sticky-type="reduce-logo-size"]',
    );

  const ensurePlaceholder = (section) => {
    let placeholder = section.previousElementSibling;

    if (!placeholder?.classList.contains('cp-mobile-sticky-placeholder')) {
      placeholder = document.createElement('span');
      placeholder.className = 'cp-mobile-sticky-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      section.before(placeholder);
    }

    return placeholder;
  };

  const reinforceAlwaysState = (section) => {
    section.classList.add('shopify-section-header-sticky');
    section.classList.remove(
      'shopify-section-header-hidden',
      'shopify-section-header-animate',
      'animate',
    );
  };

  const clearManagedState = (section) => {
    const placeholder = section.previousElementSibling?.classList.contains('cp-mobile-sticky-placeholder')
      ? section.previousElementSibling
      : null;

    section.classList.remove(
      'cp-mobile-preview-managed',
      'cp-mobile-fixed-sticky',
    );

    if (placeholder) placeholder.style.height = '0px';
    metrics.delete(section);
  };

  const measureSection = (section, stickyHeader) => {
    if (isThemeEditor() || !mobileQuery.matches) return null;

    const placeholder = ensurePlaceholder(section);

    /* Force a true normal-flow measurement; do not measure a sticky/fixed element. */
    section.classList.add('cp-mobile-preview-managed');
    section.classList.remove('cp-mobile-fixed-sticky');
    placeholder.style.height = '0px';

    const scrollTop = getScrollTop();
    const rect = section.getBoundingClientRect();
    const height = section.offsetHeight || stickyHeader.offsetHeight || rect.height || 0;
    const stickyOffset = getStickyOffset();
    const originalTop = rect.top + scrollTop;
    const originalBottom = originalTop + height;

    const value = {
      placeholder,
      height,
      fixAt: Math.max(0, originalTop - stickyOffset),
      scrolledAt: Math.max(0, originalBottom - stickyOffset),
    };

    metrics.set(section, value);
    return value;
  };

  const getMetrics = (section, stickyHeader) => metrics.get(section) || measureSection(section, stickyHeader);

  const syncAlwaysStickyHeader = () => {
    const scrollTop = getScrollTop();
    const editor = isThemeEditor();
    const mobile = mobileQuery.matches;

    getAlwaysStickyHeaders().forEach((stickyHeader) => {
      const section = stickyHeader.closest('.section-header');
      if (!section) return;

      reinforceAlwaysState(section);

      if (editor || !mobile) {
        clearManagedState(section);
        return;
      }

      section.classList.add('cp-mobile-preview-managed');
      const state = getMetrics(section, stickyHeader);
      if (!state) return;

      const shouldFix = scrollTop > state.fixAt;
      const hasPassedOriginalHeader = scrollTop >= state.scrolledAt;

      if (shouldFix) {
        state.placeholder.style.height = `${state.height}px`;
        section.classList.add('cp-mobile-fixed-sticky');
      } else {
        state.placeholder.style.height = '0px';
        section.classList.remove('cp-mobile-fixed-sticky');
      }

      section.classList.toggle('scrolled-past-header', hasPassedOriginalHeader);

      /* Native Dawn must never hide an Always header between our scroll frames. */
      section.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-animate',
        'animate',
      );
    });
  };

  const scheduleSync = () => {
    if (frameId) return;

    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      syncAlwaysStickyHeader();
    });
  };

  const remeasureAndSync = () => {
    getAlwaysStickyHeaders().forEach((stickyHeader) => {
      const section = stickyHeader.closest('.section-header');
      if (!section) return;

      if (isThemeEditor() || !mobileQuery.matches) {
        clearManagedState(section);
        return;
      }

      metrics.delete(section);
    });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(syncAlwaysStickyHeader);
    });
  };

  const init = () => {
    remeasureAndSync();

    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', remeasureAndSync, { passive: true });
    window.addEventListener('orientationchange', remeasureAndSync, { passive: true });
    window.addEventListener('load', remeasureAndSync, { once: true });
    window.addEventListener('pageshow', remeasureAndSync);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', remeasureAndSync);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(remeasureAndSync);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', remeasureAndSync);
})();
